import { useState, useEffect, useCallback, useRef } from 'react';
import { WebSocketCallService } from '../services/websocketCallService/WebSocketCallService';
import { WebSocketCallServiceConfig } from '@shared/services/websocketCallService';
import { AgentTranscriptData } from '@shared/services/websocketCallService/types';
import { currentTimestamp } from '@shared/utils/DateUtil';
import { ConversationLogDto } from '@shared/services/conversationApiService/apiService';

export interface UseWebSocketCallServiceReturn {
  isConnected: boolean;
  isSessionReady: boolean;
  isRecording: boolean;
  callId: string | null;
  error: string | null;
  transcripts: ConversationLogDto[] | null;

  startCall: () => Promise<void>;
  endCall: () => Promise<void>;
  disconnect: () => void;

  onTranscriptReceived: (callback: (text: string, isFinal: boolean) => void) => void;
}

export const useWebSocketCallService = (
  config: WebSocketCallServiceConfig,
): UseWebSocketCallServiceReturn => {
  const [isConnected, setIsConnected] = useState(false);
  const [currentTranscriptionId, setCurrentTranscriptionId] = useState(0);
  const currentTranscriptionIdRef = useRef(0);

  useEffect(() => {
    currentTranscriptionIdRef.current = currentTranscriptionId;
  }, [currentTranscriptionId]);
  const [isSessionReady, setIsSessionReady] = useState(false);
  const [isRecording, setIsRecording] = useState(false);
  const [callId, setCallId] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [transcripts, setTranscripts] = useState<ConversationLogDto[] | null>([]);

  const serviceRef = useRef<WebSocketCallService | null>(null);
  const transcriptCallbackRef = useRef<((text: string, final: boolean) => void) | null>(null);

  useEffect(() => {
    const svc = new WebSocketCallService({
      ...config,

      onConnectedToServer: () => {
        setIsConnected(true);
      },

      onSessionReady: (data) => {
        setIsSessionReady(true);
        setCallId(data.callId);
      },

      onTranscriptReceived: (data: AgentTranscriptData) => {
        // Fire external callback if provided
        transcriptCallbackRef.current?.(data.text, !!data.final);

        const now = currentTimestamp().toISOString();
        const id = String(currentTranscriptionIdRef.current);

        setTranscripts((prev) => {
          const list = prev ?? [];

          if (data.final) {
            // If final is true, complete current entry and prepare for next
            const idx = list.findIndex((l) => l.id === id);

            if (idx >= 0) {
              // Update current entry with final text
              const last = list[idx];
              const updated: ConversationLogDto = {
                ...last,
                message: last.message ? `${last.message} ${data.text}` : data.text,
                updatedAt: now,
              };
              const next = [...list];
              next[idx] = updated;
              currentTranscriptionIdRef.current += 1;
              setCurrentTranscriptionId(currentTranscriptionIdRef.current);
              return next;
            } else {
              // Create new entry with final text
              const log: ConversationLogDto = {
                id,
                conversationId: callId ?? '',
                actor: 'agent',
                message: data.text,
                createdAt: now,
                updatedAt: now,
              };
              currentTranscriptionIdRef.current += 1;
              setCurrentTranscriptionId(currentTranscriptionIdRef.current);
              return [...list, log];
            }
          } else {
            // If final is false, update current entry
            const idx = list.findIndex((l) => l.id === id);

            if (idx >= 0) {
              // Update existing entry
              const last = list[idx];
              const updated: ConversationLogDto = {
                ...last,
                message: last.message ? `${last.message} ${data.text}` : data.text,
                updatedAt: now,
              };
              const next = [...list];
              next[idx] = updated;
              return next;
            } else {
              // Create new entry
              const log: ConversationLogDto = {
                id,
                conversationId: callId ?? '',
                actor: 'agent',
                message: data.text,
                createdAt: now,
                updatedAt: now,
              };
              return [...list, log];
            }
          }
        });
      },

      onError: (err) => {
        console.error('WebSocket Call Service error:', err);
        setError(err?.message ?? 'Unknown error');
      },

      onDisconnect: () => {
        setIsConnected(false);
        setIsSessionReady(false);
        setIsRecording(false);
        setCallId(null);
        setTranscripts(null);
      },
    });

    serviceRef.current = svc;

    return () => {
      svc.destroy();
      serviceRef.current = null;
    };
  }, []);

  // Start: connect WS + init audio + start capture/stream
  const startCall = useCallback(async () => {
    if (!serviceRef.current) throw new Error('Service not initialized');
    setError(null);
    await serviceRef.current.startCall();
    setIsRecording(true);
    setIsConnected(true);
  }, []);

  const endCall = useCallback(async () => {
    if (!serviceRef.current) return;
    await serviceRef.current.endCall();
    setIsSessionReady(false);
    setIsRecording(false);
    setCallId(null);
    setTranscripts(null);
    setIsConnected(false);
  }, []);

  const disconnect = useCallback(() => {
    serviceRef.current?.destroy();
    serviceRef.current = null;
    setIsConnected(false);
    setIsSessionReady(false);
    setIsRecording(false);
    setCallId(null);
    setTranscripts(null);
  }, []);

  const onTranscriptReceived = useCallback((cb: (text: string, final: boolean) => void) => {
    transcriptCallbackRef.current = cb;
  }, []);

  return {
    isConnected,
    isSessionReady,
    isRecording,
    callId,
    error,
    transcripts,
    startCall,
    endCall,
    disconnect,
    onTranscriptReceived,
  };
};
